const API_BASE = '/api';

// 获取存储的token
const getToken = () => localStorage.getItem('token');

// 通用请求方法
async function request(url, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('未授权，请重新登录');
  }

  if (!response.ok) {
    throw new Error(data.message || '请求失败');
  }

  return data;
}

// Auth API
export const authApi = {
  login: (username, password) =>
    request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  getProfile: () => request('/auth/profile'),
  updateProfile: (data) =>
    request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  changePassword: (oldPassword, newPassword) =>
    request('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    }),
};

// User API
export const userApi = {
  getList: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/users?${query}`);
  },
  get: (id) => request(`/users/${id}`),
  create: (data) =>
    request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) => request(`/users/${id}`, { method: 'DELETE' }),
  getDevelopers: () => request('/users/developers'),
  getSales: () => request('/users/sales'),
};

// Account API
export const accountApi = {
  getList: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/accounts?${query}`);
  },
  get: (id) => request(`/accounts/${id}`),
  create: (data) =>
    request('/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    request(`/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) => request(`/accounts/${id}`, { method: 'DELETE' }),
  getAvailable: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/accounts/available${query ? `?${query}` : ''}`);
  },
  getStatistics: () => request('/accounts/statistics'),
  get2faToken: (id) => request(`/accounts/${id}/2fa-token`),
  getOwners: () => request('/accounts/owners'),
  getSalesPersons: () => request('/accounts/sales-persons'),
  getRandom: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/accounts/random?${query}`);
  },
  quickSale: (accountId, data) =>
    request(`/accounts/${accountId}/quick-sale`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  batchImport: (data) =>
    request('/accounts/batch-import', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  batchUpdateStatus: (ids, status) =>
    request('/accounts/batch-update', {
      method: 'POST',
      body: JSON.stringify({ account_ids: ids, status }),
    }),
  batchUpdate: (data) =>
    request('/accounts/batch-update', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  batchGetDetails: (ids) =>
    request('/accounts/batch-details', {
      method: 'POST',
      body: JSON.stringify({ account_ids: ids }),
    }),
};

// Sales API
export const salesApi = {
  getList: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/sales?${query}`);
  },
  get: (id) => request(`/sales/${id}`),
  create: (data) =>
    request('/sales', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    request(`/sales/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) => request(`/sales/${id}`, { method: 'DELETE' }),
  markDelivered: (id) => request(`/sales/${id}/deliver`, { method: 'POST', body: '{}' }),
  markSettled: (id) => request(`/sales/${id}/settle`, { method: 'POST' }),
  bind: (id, accountId) =>
    request(`/sales/${id}/bind`, {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId }),
    }),
  unbind: (id) => request(`/sales/${id}/unbind`, { method: 'POST' }),
  getStatistics: () => request('/sales/statistics'),
};

// Email API
export const emailApi = {
  getList: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/emails?${query}`);
  },
  send: (data) =>
    request('/emails', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  sendForSales: (recordId, data) =>
    request(`/emails/sales/${recordId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  sendForAccount: (accountId, data) =>
    request(`/emails/account/${accountId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Log API
export const logApi = {
  getList: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/logs?${query}`);
  },
  getTypes: () => request('/logs/types'),
};

// Version API
export const versionApi = {
  getList: () => request('/versions'),
  getLatest: () => request('/versions/latest'),
  create: (data) =>
    request('/versions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Document API
export const documentApi = {
  getList: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/documents?${query}`);
  },
  get: (id) => request(`/documents/${id}`),
  create: (data) =>
    request('/documents', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    request(`/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id) => request(`/documents/${id}`, { method: 'DELETE' }),
};

// Permission API
export const permissionApi = {
  list: (resourceType, resourceId) =>
    request(`/permissions?resource_type=${resourceType}&resource_id=${resourceId}`),
  grant: (data) =>
    request('/permissions/grant', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  revoke: (permissionId) =>
    request('/permissions/revoke', {
      method: 'POST',
      body: JSON.stringify({ permission_id: permissionId })
    })
};

// Cost API
export const costApi = {
  // 成本记录
  getRecords: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/cost/records?${query}`);
  },
  getRecord: (id) => request(`/cost/records/${id}`),
  createRecord: (data) => request('/cost/records', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateRecord: (id, data) => request(`/cost/records/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deleteRecord: (id) => request(`/cost/records/${id}`, {
    method: 'DELETE'
  }),

  // 渠道管理
  getChannels: () => request('/cost/channels'),
  createChannel: (data) => request('/cost/channels', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateChannel: (id, data) => request(`/cost/channels/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  reorderChannels: (data) => request('/cost/channels/reorder', {
    method: 'PUT',
    body: JSON.stringify(data)
  }),

  // 规则管理
  getRules: (params) => {
    const query = new URLSearchParams(params).toString();
    return request(`/cost/rules?${query}`);
  },
  createRule: (data) => request('/cost/rules', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateRule: (id, data) => request(`/cost/rules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  reorderRules: (data) => request('/cost/rules/reorder', {
    method: 'PUT',
    body: JSON.stringify(data)
  }),

  // 结算
  previewSettlement: (data) => request('/cost/settlement/preview', {
    method: 'POST',
    body: JSON.stringify(data || {})
  }),
  confirmSettlement: (data) => request('/cost/settlement/confirm', {
    method: 'POST',
    body: JSON.stringify(data || {})
  })
};
